import React from 'react';
import { Star } from 'lucide-react';
import { HousePanel, HouseStatCard, type KitIcon } from './trackerKit';
import { RAG_LABEL, type RagTone } from '../../../hooks/pipelineTracker/targetsContract';

/**
 * Small pieces shared by the Operations Overview and the Pipeline tab's compliance snapshot.
 *
 * Kept out of pipelineParts.tsx so this work does not edit a file every phase shares; the visual
 * language is pipelineParts' own (SURFACE).
 *
 * NOTHING HERE HOLDS A FIGURE. A metric that cannot be computed yet is not drawn as a tile at all:
 * it is named in its tab's "Coming when the data is available" list (pipelineParts' ComingSoonList).
 */

/** '2026-09-21' to '21 Sep 2026'. Parsed as UTC so a label cannot slip a day. */
export function prettyDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** '2026-09' to 'September 2026'. */
export function monthName(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-AU', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * '2026-09' to 'Sep 26', for chart axes. A fixed three-letter list rather than the locale's short
 * month, which is 'Sept' in en-AU on some runtimes and 'Sep' on others: an axis label should not
 * change width with the browser.
 */
export function shortMonth(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m || m < 1 || m > 12) return ym;
  return `${SHORT_MONTHS[m - 1]} ${String(y).slice(2)}`;
}

/** '2026-09' to '2026-08'. */
export function previousMonth(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
}

/** '2026-09-01', '2026-09-30' to '1 to 30 Sep'; across months, '15 Aug to 14 Sep'. */
export function shortRange(start: string, end: string): string {
  const parse = (iso: string) => iso.split('-').map(Number);
  const [sy, sm, sd] = parse(start);
  const [ey, em, ed] = parse(end);
  if (!sm || !sd || !em || !ed || sm > 12 || em > 12) return `${start} to ${end}`;
  return sy === ey && sm === em
    ? `${sd} to ${ed} ${SHORT_MONTHS[em - 1]}`
    : `${sd} ${SHORT_MONTHS[sm - 1]} to ${ed} ${SHORT_MONTHS[em - 1]}`;
}

/**
 * This month's entered figure against last month's, as one short line: "+3 vs Aug", "No change vs
 * Aug", or "No Aug entry" when last month has no figure. Never a comparison against a guess.
 * `unit` follows the number ("pts" for the audit result, a percentage-point change).
 */
export function monthOnMonth(
  current: number,
  previous: number | undefined,
  previousYm: string,
  options: { decimals?: number; unit?: string } = {},
): string {
  const label = shortMonth(previousYm).split(' ')[0];
  if (previous === undefined) return `No ${label} entry`;
  const scale = 10 ** (options.decimals ?? 0);
  const diff = Math.round((current - previous) * scale) / scale;
  if (diff === 0) return `No change vs ${label}`;
  const figure = diff.toLocaleString('en-AU', { maximumFractionDigits: options.decimals ?? 0 });
  return `${diff > 0 ? '+' : ''}${figure}${options.unit ? ` ${options.unit}` : ''} vs ${label}`;
}

export type GlanceTone = 'brand' | 'green' | 'amber' | 'violet' | 'red';

/**
 * The tones of pipelineParts' Tile, so these tiles read as the same family as the Pipeline tab's
 * headline row. Repeated here rather than exported from that shared file. `red` is this tab's own,
 * for the overdue register.
 */
const GLANCE_TONE: Record<GlanceTone, { rgb: string; bar: string; border: string; label: string; value: string }> = {
  brand: { rgb: '37,99,235', bar: '#2563eb', border: '#304e8c', label: '#7b9eeb', value: '#60a5fa' },
  green: { rgb: '22,163,74', bar: '#16a34a', border: '#2a664e', label: '#73c292', value: '#34d399' },
  amber: { rgb: '245,158,11', bar: '#f59e0b', border: '#7f6436', label: '#eebf70', value: '#fbbf24' },
  violet: { rgb: '124,58,237', bar: '#7c3aed', border: '#513e8c', label: '#ab88ec', value: '#a78bfa' },
  red: { rgb: '239,68,68', bar: '#ef4444', border: '#7f3a3a', label: '#f19c9c', value: '#f87171' },
};

export const glanceToneColour = (tone: GlanceTone): string => GLANCE_TONE[tone].bar;

/** A tile's grade against its target: the line under the footer, with its status dot. */
export interface GlanceGrade {
  tone: RagTone;
  /** "Target 4.8 · 95%": the target and the attainment the tone was taken from. */
  text: string;
}

const RAG_DOT: Record<RagTone, string> = {
  green: 'bg-green-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
};

const RAG_TEXT: Record<RagTone, string> = {
  green: 'text-green-300',
  amber: 'text-amber-300',
  red: 'text-red-300',
};

/**
 * The status dot and its line. Colour is never the only signal: the grade is also a word, beside
 * the target and percentage it was taken from, as on the Deal movements tiles.
 */
export const GradeLine: React.FC<{ grade: GlanceGrade }> = ({ grade }) => (
  <span
    data-testid="glance-grade"
    data-rag={grade.tone}
    className="mt-1 flex items-center gap-1.5 text-[0.6875rem] tabular-nums leading-snug"
  >
    <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${RAG_DOT[grade.tone]}`} />
    <span className={`truncate ${RAG_TEXT[grade.tone]}`}>{grade.text}</span>
    <span className={`shrink-0 font-semibold ${RAG_TEXT[grade.tone]}`}>{RAG_LABEL[grade.tone]}</span>
  </span>
);

/**
 * One tile of the Operations "This month at a glance" row. Every tile has the same four slots in
 * the same place: label, value, a small visual, and a one-line footer pinned to the bottom, so a
 * row of them is equal in height and reads as one row whatever each holds.
 *
 * A figure that is absent is said to be absent (`emptyValue`, "Not entered" by default), never
 * drawn as zero: a practice that has not typed its rating has not got 0 stars.
 */
export const GlanceTile: React.FC<{
  label: string;
  value: string | null;
  tone: GlanceTone;
  emptyValue?: string;
  /** A small picture of the value (stars, a progress bar). Its slot is kept when absent. */
  visual?: React.ReactNode;
  /** One short line. Longer detail goes in `detail`. */
  footer: React.ReactNode;
  /** The full statement behind the footer: the tile's hover title and screen-reader text. */
  detail?: string;
  /** A small link beside the label, e.g. Open Tasks. Beside the label so every footer lines up. */
  action?: React.ReactNode;
  icon?: KitIcon;
  /** Its grade against a target, when one is set and a figure is entered. Absent: no dot at all. */
  grade?: GlanceGrade | null;
}> = ({ label, value, tone, emptyValue = 'Not entered', visual, footer, detail, action, icon, grade }) => {
  const c = GLANCE_TONE[tone];
  return (
    <HouseStatCard
      testId="glance-tile"
      dataTone={tone}
      title={detail}
      label={label}
      action={action}
      icon={icon}
      color={c.bar}
      valueColor={value === null ? undefined : c.value}
      value={
        <span className="flex h-10 items-center">
          {value === null
            ? <span className="text-base font-semibold text-gray-400">{emptyValue}</span>
            : value}
        </span>
      }
      visual={<div className="flex h-4 w-full items-center">{visual}</div>}
      footer={
        <>
          <div className="truncate">{footer}</div>
          {grade && <GradeLine grade={grade} />}
          {detail && <span className="sr-only">{detail}</span>}
        </>
      }
    />
  );
};

/** A thin bar for a part of a whole, e.g. tasks done of due, or an audit result out of 100. */
export const ProgressBar: React.FC<{ fraction: number; colour: string; label: string }> = ({ fraction, colour, label }) => (
  <div role="img" aria-label={label} className="h-1.5 w-full overflow-hidden rounded-full bg-gray-900/70">
    <div className="h-full rounded-full" style={{ width: `${Math.max(0, Math.min(1, fraction)) * 100}%`, background: colour }} />
  </div>
);

/** A Google rating out of five as five stars, each filled to its part of the rating. */
export const StarRating: React.FC<{ rating: number }> = ({ rating }) => (
  <div role="img" aria-label={`${rating.toFixed(1)} out of 5 stars`} className="flex items-center gap-0.5">
    {[0, 1, 2, 3, 4].map((i) => {
      const fill = Math.max(0, Math.min(1, rating - i));
      return (
        <span key={i} className="relative inline-block h-3.5 w-3.5">
          <Star aria-hidden="true" className="absolute inset-0 h-3.5 w-3.5 text-gray-600" />
          <span className="absolute inset-y-0 left-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
            <Star aria-hidden="true" className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
          </span>
        </span>
      );
    })}
  </div>
);

/**
 * The last few months' entered figures as small columns, oldest first, the latest in the tile's
 * colour. A month with no entry is a flat grey mark, not a zero-height bar that reads as zero.
 * Scaled between the lowest and highest entry, so a running total that grows by a few still shows.
 */
export const EntryHistory: React.FC<{
  points: Array<{ ym: string; value: number | undefined }>;
  colour: string;
  noun: string;
}> = ({ points, colour, noun }) => {
  const entered = points.map((p) => p.value).filter((v): v is number => v !== undefined);
  const lo = Math.min(...entered);
  const hi = Math.max(...entered);
  const label = points
    .map((p) => `${shortMonth(p.ym)}: ${p.value === undefined ? 'no entry' : p.value.toLocaleString('en-AU')}`)
    .join(', ');
  return (
    <div role="img" aria-label={`${noun} by month. ${label}`} className="flex h-4 items-end gap-1">
      {points.map((p, i) => {
        const last = i === points.length - 1;
        if (p.value === undefined) {
          return <span key={p.ym} className="h-0.5 w-2 rounded-full bg-gray-600" />;
        }
        const height = hi === lo ? 1 : 0.3 + (0.7 * (p.value - lo)) / (hi - lo);
        return (
          <span
            key={p.ym}
            className="w-2 rounded-sm"
            style={{ height: `${height * 100}%`, background: colour, opacity: last ? 1 : 0.45 }}
          />
        );
      })}
    </div>
  );
};

/**
 * A Panel in one of the tile tones, rendered through the house HousePanel (trackerKit): a tone bar
 * across the top, the header tinted toward the tone and an icon in a tone chip.
 */
export const TonePanel: React.FC<{
  title: string;
  tone: GlanceTone;
  icon?: KitIcon;
  badge?: React.ReactNode;
  blurb?: React.ReactNode;
  actions?: React.ReactNode;
  /** The tone carries meaning, so it stands inside a TrackerSection of another colour (HousePanel). */
  semantic?: boolean;
  children: React.ReactNode;
}> = ({ title, tone, icon, badge, blurb, actions, semantic, children }) => (
  <HousePanel
    title={title}
    color={GLANCE_TONE[tone].bar}
    semantic={semantic}
    icon={icon}
    badge={badge}
    blurb={blurb}
    actions={actions}
    dataTone={tone}
  >
    {children}
  </HousePanel>
);

/** A subtle brand-tinted row hover for the tab's tables. Never a white or grey box. */
export const ROW_HOVER = 'transition-colors hover:bg-blue-500/[0.06]';

/** One colour per compliance group, so a group reads the same in the register and the watchlist. */
const GROUP_TONE: Record<string, GlanceTone> = {
  annual_review: 'brand',
  interest_only_expiry: 'violet',
  facility_expiry: 'amber',
};

export const groupTone = (key: string): GlanceTone => GROUP_TONE[key] ?? 'brand';

/** A compliance group's name as a pill in its group colour. */
export const GroupPill: React.FC<{ groupKey: string; children: React.ReactNode }> = ({ groupKey, children }) => {
  const c = GLANCE_TONE[groupTone(groupKey)];
  return (
    <span
      data-group-tone={groupTone(groupKey)}
      className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold"
      style={{ background: `rgba(${c.rgb},0.14)`, borderColor: `rgba(${c.rgb},0.35)`, color: c.value }}
    >
      <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full" style={{ background: c.bar }} />
      {children}
    </span>
  );
};

/** Days on a register row: red when overdue, amber when due within 30 days, blue otherwise. */
export const DaysChip: React.FC<{ days: number; overdue: boolean }> = ({ days, overdue }) => {
  const kind = overdue ? 'overdue' : days < 30 ? 'soon' : 'later';
  const cls = {
    overdue: 'bg-red-500/15 text-red-300 border-red-500/30',
    soon: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    later: 'bg-blue-500/10 text-blue-300 border-blue-500/25',
  }[kind];
  return (
    <span data-days={kind} className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold tabular-nums ${cls}`}>
      {days}d
    </span>
  );
};
